import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import * as E from 'react-email'
import { data, redirect, Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { findUserByIdentifier } from '#app/utils/login-code.server.ts'
import { type SEOHandle } from '#app/utils/seo.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/forgot-password.ts'
import { getRedirectToUrl, prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
  usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData()
  await checkHoneypot(formData)
  const submission = parseWithZod(formData, { schema: ForgotPasswordSchema })
  if (submission.status !== 'success') {
    return data(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }
  const usernameOrEmail = submission.value.usernameOrEmail.toLowerCase()

  // Same response whether or not the account exists, so this can't be used to
  // find out who is registered.
  const user = await findUserByIdentifier(usernameOrEmail)
  const { redirectTo, verifyUrl, otp } = user
    ? await prepareVerification({
        period: 10 * 60,
        request,
        type: 'reset-password',
        target: usernameOrEmail,
      })
    : {
        redirectTo: getRedirectToUrl({
          request,
          type: 'reset-password',
          target: usernameOrEmail,
        }),
        verifyUrl: null,
        otp: null,
      }

  if (user && verifyUrl && otp) {
    // Not awaited: how long sending takes must not reveal that the account
    // exists, and a failure can't be reported for the same reason, so it's logged.
    void sendEmail({
      to: user.email,
      subject: `Password reset code: ${otp}`,
      react: (
        <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
      ),
    }).then((response) => {
      if (response.status === 'error') {
        console.error('Failed to send password reset email:', response.error)
      }
    })
  }
  return redirect(redirectTo.toString())
}

function ForgotPasswordEmail({
  onboardingUrl,
  otp,
}: {
  onboardingUrl: string
  otp: string
}) {
  return (
    <E.Html lang="en" dir="ltr">
      <E.Container>
        <h1>
          <E.Text>Youth Rollcall Password Reset</E.Text>
        </h1>
        <p>
          <E.Text>
            Here's your verification code: <strong>{otp}</strong>
          </E.Text>
        </p>
        <p>
          <E.Text>Or click the link:</E.Text>
        </p>
        <E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
      </E.Container>
    </E.Html>
  )
}

export const meta: Route.MetaFunction = () => {
  return [{ title: 'Password Recovery for Youth Rollcall' }]
}

export default function ForgotPasswordRoute() {
  const forgotPassword = useFetcher<typeof action>()

  const [form, fields] = useForm({
    id: 'forgot-password-form',
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <div className="container pt-20 pb-32">
      <div className="flex flex-col justify-center">
        <div className="text-center">
          <h1 className="text-h1">Forgot Password</h1>
          <p className="text-body-md text-muted-foreground mt-3">
            No worries, we'll send you reset instructions.
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-sm min-w-full sm:min-w-[368px]">
          <forgotPassword.Form method="POST" {...getFormProps(form)}>
            <HoneypotInputs />
            <div>
              <Field
                labelProps={{
                  htmlFor: fields.usernameOrEmail.id,
                  children: 'Username or Email',
                }}
                inputProps={{
                  autoFocus: true,
                  ...getInputProps(fields.usernameOrEmail, { type: 'text' }),
                }}
                errors={fields.usernameOrEmail.errors}
              />
            </div>
            <ErrorList errors={form.errors} id={form.errorId} />

            <div className="mt-6">
              <StatusButton
                className="w-full"
                status={
                  forgotPassword.state === 'submitting'
                    ? 'pending'
                    : (form.status ?? 'idle')
                }
                type="submit"
                disabled={forgotPassword.state !== 'idle'}
              >
                Recover password
              </StatusButton>
            </div>
          </forgotPassword.Form>
          <Link
            to="/login"
            className="text-body-sm mt-11 text-center font-semibold"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
